import { useMemo } from 'react';

import { useFormatter } from '@core/js/hooks/useFormatter';

import { useDistributorByTemplate } from '@interlayer/js/app/components/main/Registration/hooks/useDistributorByTemplate';
import { getRefereeCookieForReg } from '@interlayer/js/app/components/main/Registration/utils/getRefereeCookieForReg';
import { useGetOccupationTags } from '@interlayer/js/app/components/popups/OccupationPopup/components/OccupationForm/hooks/useGetOccupationTags';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { STEP_NAMES } from '@interlayer/js/app/store/reducers/Registration/constants';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { TBuyaPowaConfigTagConfig } from '@interlayer/js/components/system/TagManager/buzz/types';
import { PLAYER_TAG_TYPES } from '@interlayer/js/hooks/user/store/buzz/constants';
import { IPlayerTag } from '@interlayer/js/hooks/user/store/buzz/types';

export const useRegPlayerTag = () => {
    const formatter = useFormatter();
    const getOccupationTags = useGetOccupationTags();
    const buyaPowaConfig = useConfigTag(CONFIG_TAG_TYPE.buyaPowaConfig) as TBuyaPowaConfigTagConfig;
    const registerPlayerTag = buyaPowaConfig?.config?.registerPlayerTag;
    const {
        registrationsData: {
            [STEP_NAMES.SECURITY_STEP]: { occupation }
        },
        config: {
            tagsConfiguration,
            enablePlayerTags,
            withOccupation,
            isOccupationMandatory
        },
        buyapowaUserId
    } = useRegistrationSelector();
    const [
        dTagOccupationMandatoryPlayerTag = 'Athena/MandatoryOccupation',
        dTagOccupationOptionalProvidedPlayerTag = 'Athena/OptionalOccupation/Provided',
        dTagOccupationOptionalNonePlayerTag = 'Athena/OptionalOccupation/None',
        dTagVerifyMarketingPreferencesPlayerTag = 'Athena/marketingprefs'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.occupationMandatoryPlayerTag,
        DYNAMIC_TAG_NAMES.occupationOptionalProvidedPlayerTag,
        DYNAMIC_TAG_NAMES.occupationOptionalNonePlayerTag,
        DYNAMIC_TAG_NAMES.verifyMarketingPreferencesPlayerTag
    ]);

    const isOccupationInReg = useDistributorByTemplate({
        retail: false,
        slot: false,
        online: true
    });

    return useMemo(() => {
        let allTags: IPlayerTag[] = [];

        if (enablePlayerTags) {
            allTags = tagsConfiguration
                .map((tag) => {
                    if (tag.type === PLAYER_TAG_TYPES.SIMPLE) {
                        return { name: tag.name };
                    }

                    const date = new Date();
                    date.setDate(date.getDate() + Number(tag.expirationDate));

                    return {
                        ...tag,
                        ...tag.expirationDate && {
                            expirationDate: formatter.date(date)
                        }
                    };
                });
        }

        if (isOccupationInReg && withOccupation) {
            const optionalTag = occupation
                ? dTagOccupationOptionalProvidedPlayerTag
                : dTagOccupationOptionalNonePlayerTag;
            const [name, value] = (
                isOccupationMandatory
                    ? dTagOccupationMandatoryPlayerTag
                    : optionalTag
            ).split('=');

            allTags = [
                ...allTags,
                ...getOccupationTags(occupation),
                {
                    name,
                    ...value && {
                        value,
                        type: 'text'
                    }
                }
            ];
        }

        const rafCookie = getRefereeCookieForReg();

        if (rafCookie) {
            allTags = allTags.concat(rafCookie);
        }

        if (dTagVerifyMarketingPreferencesPlayerTag) {
            allTags.push({ name: dTagVerifyMarketingPreferencesPlayerTag });
        }

        if (buyapowaUserId && registerPlayerTag) {
            allTags.push({ name: registerPlayerTag, value: buyapowaUserId, type: PLAYER_TAG_TYPES.TEXT });
        }

        if (allTags.length > 0) {
            return allTags;
        }
    }, [
        registerPlayerTag,
        buyapowaUserId,
        formatter,
        occupation,
        withOccupation,
        enablePlayerTags,
        tagsConfiguration,
        getOccupationTags,
        isOccupationInReg,
        isOccupationMandatory,
        dTagOccupationMandatoryPlayerTag,
        dTagOccupationOptionalNonePlayerTag,
        dTagOccupationOptionalProvidedPlayerTag,
        dTagVerifyMarketingPreferencesPlayerTag
    ]);
};
